import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Eye, RefreshCw, XCircle, ArrowRightLeft, CalendarPlus, Bot, CheckCircle } from 'lucide-react'
import { subscriptionsApi, plansApi } from '../../lib/api'
import DataTable, { Column } from '../../components/shared/DataTable'
import StatusBadge from '../../components/shared/StatusBadge'
import PlanBadge from '../../components/shared/PlanBadge'
import FilterBar from '../../components/shared/FilterBar'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import DetailDrawer from '../../components/shared/DetailDrawer'
import Pagination from '../../components/shared/Pagination'
import Timeline from '../../components/shared/Timeline'
import ErrorState from '../../components/shared/ErrorState'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { formatDate, formatDatetime } from '../../lib/utils'
import type { Subscription } from '../../types'

import { tx } from '../../lib/i18n'
const statusOptions = [
  { label: tx('Active'), value: 'active' },
  { label: tx('Trial'), value: 'trial' },
  { label: tx('Past Due'), value: 'past_due' },
  { label: tx('Cancelled'), value: 'cancelled' },
  { label: tx('Suspended'), value: 'suspended' },
  { label: tx('Expired'), value: 'expired' },
]

const ADDON_NAMES: Record<string, string> = { ai_growth: 'AI Growth', ai_crm: 'AI CRM', ai_full: 'AI Full Suite' }

const SubscriptionsPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()

  const [searchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState(searchParams.get('search') ?? '')
  const [statusFilter, setStatusFilter] = useState('')
  const [planFilter, setPlanFilter] = useState('')

  const [pickedSub, setSelectedSub] = useState<Subscription | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const [changePlanModal, setChangePlanModal] = useState(false)
  const [addonsModal, setAddonsModal] = useState(false)
  const [addonPick, setAddonPick] = useState<string[]>([])
  const [addonMonths, setAddonMonths] = useState('1')
  const [extendModal, setExtendModal] = useState(false)
  const [cancelDialog, setCancelDialog] = useState(false)
  const [reactivateDialog, setReactivateDialog] = useState(false)

  const [newPlanId, setNewPlanId] = useState('')
  const [extendDays, setExtendDays] = useState('30')
  const [customDays, setCustomDays] = useState('')
  const [reason, setReason] = useState('')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscriptions', { page, search, statusFilter, planFilter }],
    queryFn: () =>
      subscriptionsApi
        .list({ page, limit: 20, search, status: statusFilter || undefined, planId: planFilter || undefined })
        .then((r) => r.data),
  })

  const { data: plansData } = useQuery({
    queryKey: ['plans'],
    queryFn: () => plansApi.list().then((r) => r.data.data),
  })

  const plans = plansData ?? []

  const changePlanMutation = useMutation({
    mutationFn: () => subscriptionsApi.changePlan(selectedSub!.id, newPlanId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      showToast({ type: 'success', message: tx('Plan changed successfully.') })
      setChangePlanModal(false)
      setReason('')
    },
    onError: (err: { response?: { data?: { error?: string } } }) =>
      showToast({ type: 'error', message: err.response?.data?.error ?? 'Failed to change plan.' }),
  })

  const addonsMutation = useMutation({
    mutationFn: () => subscriptionsApi.setAddons(selectedSub!.id, addonPick, Number(addonMonths) || 0, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      setAddonsModal(false)
      setSelectedSub((cur) => (cur ? { ...cur, addons: addonPick, addonExpiresAt: Number(addonMonths) > 0 && addonPick.length ? new Date(Date.now() + Number(addonMonths) * 30 * 86_400_000).toISOString() : null } : cur))
    },
  })

  const extendMutation = useMutation({
    mutationFn: () => {
      const days = extendDays === 'custom' ? parseInt(customDays) : parseInt(extendDays)
      return subscriptionsApi.extend(selectedSub!.id, days, reason)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      showToast({ type: 'success', message: tx('Subscription extended.') })
      setExtendModal(false)
      setReason('')
    },
    onError: (err: { response?: { data?: { error?: string } } }) =>
      showToast({ type: 'error', message: err.response?.data?.error ?? 'Failed to extend subscription.' }),
  })

  const cancelMutation = useMutation({
    mutationFn: () => subscriptionsApi.cancel(selectedSub!.id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      showToast({ type: 'success', message: tx('Subscription cancelled.') })
      setCancelDialog(false)
      setReason('')
    },
    onError: (err: { response?: { data?: { error?: string } } }) =>
      showToast({ type: 'error', message: err.response?.data?.error ?? 'Failed to cancel subscription.' }),
  })

  const reactivateMutation = useMutation({
    mutationFn: () => subscriptionsApi.reactivate(selectedSub!.id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      showToast({ type: 'success', message: tx('Subscription reactivated.') })
      setReactivateDialog(false)
      setReason('')
    },
    onError: (err: { response?: { data?: { error?: string } } }) =>
      showToast({ type: 'error', message: err.response?.data?.error ?? 'Failed to reactivate.' }),
  })

  const subs = data?.data ?? []
  // follow the live list so the drawer shows new dates / plan / status right after a change
  const selectedSub = (pickedSub && (subs as Subscription[]).find((x) => x.id === pickedSub.id)) ?? pickedSub
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const planOptions = (plansData ?? []).map((p) => ({ label: p.displayName, value: p.id }))

  const columns: Column<Subscription>[] = [
    {
      key: 'shopName',
      header: tx('Shop'),
      sortable: true,
      render: (r) => <span className="font-medium text-gray-900">{r.shopName}</span>,
    },
    {
      key: 'planName',
      header: tx('Plan'),
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <PlanBadge plan={r.planName} />
          {(r.addons ?? []).length > 0 && (
            <span className="relative group inline-flex">
              <span className="inline-flex items-center gap-0.5 rounded-full bg-purple-100 text-purple-700 px-1.5 py-0.5 text-[10px] font-semibold">
                <Bot className="w-3 h-3" /> AI
              </span>
              <span className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden min-w-[190px] rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block">
                <div className="font-semibold mb-1">{tx('AI Add-ons')}</div>
                {(r.addons ?? []).map((x) => (
                  <div key={x}>• {ADDON_NAMES[x] ?? x}</div>
                ))}
                {r.addonExpiresAt && <div className="mt-1 text-gray-300">{tx('until')} {formatDate(r.addonExpiresAt)}</div>}
              </span>
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: tx('Status'),
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: 'trialEndsAt',
      header: tx('Trial End'),
      render: (r) => <span className="text-gray-500 text-sm">{formatDate(r.trialEndsAt)}</span>,
    },
    {
      key: 'currentPeriodEnd',
      header: tx('Period End'),
      render: (r) => <span className="text-gray-500 text-sm">{formatDate(r.currentPeriodEnd)}</span>,
    },
    {
      key: 'nextBillingAt',
      header: tx('Next Billing'),
      render: (r) => <span className="text-gray-500 text-sm">{formatDate(r.nextBillingAt)}</span>,
    },
    {
      key: 'actions',
      header: tx('Actions'),
      render: (r) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            title={tx('View Details')}
            onClick={() => { setSelectedSub(r); setDrawerOpen(true) }}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <Eye className="h-4 w-4" />
          </button>
          {can('subscriptions.change_plan') && (
          <button
            title={tx('Change Plan')}
            onClick={() => { setSelectedSub(r); setNewPlanId(r.planId); setReason(''); setChangePlanModal(true) }}
            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
          >
            <ArrowRightLeft className="h-4 w-4" />
          </button>
          )}
          {can('subscriptions.extend') && (
          <button
            title={tx('Extend')}
            onClick={() => { setSelectedSub(r); setExtendDays('30'); setReason(''); setExtendModal(true) }}
            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
          >
            <CalendarPlus className="h-4 w-4" />
          </button>
          )}
          {can('subscriptions.cancel') && r.status === 'trial' && (
            <button
              title={tx('Activate (skip trial)')}
              onClick={() => { setSelectedSub(r); setReason(''); setReactivateDialog(true) }}
              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            >
              <CheckCircle className="h-4 w-4" />
            </button>
          )}
          {can('subscriptions.cancel') && (r.status === 'cancelled' || r.status === 'expired' ? (
            <button
              title={tx('Reactivate')}
              onClick={() => { setSelectedSub(r); setReason(''); setReactivateDialog(true) }}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          ) : (
            <button
              title={tx('Cancel')}
              onClick={() => { setSelectedSub(r); setReason(''); setCancelDialog(true) }}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <XCircle className="h-4 w-4" />
            </button>
          ))}
        </div>
      ),
    },
  ]

  if (error) return <ErrorState message={tx('Failed to load subscriptions.')} onRetry={() => refetch()} />

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{tx('Subscriptions')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{tx('Manage shop subscription plans and billing cycles')}</p>
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: tx('Search by shop name...') }}
        filters={[
          { key: 'status', label: tx('Status'), options: statusOptions, value: statusFilter, onChange: setStatusFilter },
          { key: 'plan', label: tx('Plan'), options: planOptions, value: planFilter, onChange: setPlanFilter },
        ]}
        onReset={() => { setSearch(''); setStatusFilter(''); setPlanFilter(''); setPage(1) }}
      />

      <div>
        <DataTable
          columns={columns}
          data={subs}
          isLoading={isLoading}
          emptyMessage={tx('No subscriptions found.')}
          onRowClick={(r) => { setSelectedSub(r); setDrawerOpen(true) }}
        />
        <Pagination page={page} totalPages={totalPages} total={total} limit={20} onPageChange={setPage} />
      </div>

      {/* Detail Drawer */}
      <DetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedSub?.shopName ?? 'Subscription Detail'}
        subtitle={selectedSub ? `Plan: ${selectedSub.planName}` : undefined}
        width="md"
      >
        {selectedSub && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: tx('Status'), value: <StatusBadge status={selectedSub.status} /> },
                { label: tx('Plan'), value: <PlanBadge plan={selectedSub.planName} /> },
                { label: tx('Period Start'), value: formatDate(selectedSub.currentPeriodStart) },
                { label: tx('Period End'), value: formatDate(selectedSub.currentPeriodEnd) },
                { label: tx('Trial Start'), value: formatDate(selectedSub.trialStartsAt) },
                { label: tx('Trial End'), value: formatDate(selectedSub.trialEndsAt) },
                { label: tx('Next Billing'), value: formatDate(selectedSub.nextBillingAt) },
                { label: tx('AI Add-ons'), value: (selectedSub.addons ?? []).length ? `${(selectedSub.addons ?? []).map((x) => ADDON_NAMES[x] ?? x).join(', ')}${selectedSub.addonExpiresAt ? ` (${tx('until')} ${formatDate(selectedSub.addonExpiresAt)})` : ''}` : '—' },
                { label: tx('Cancelled At'), value: formatDate(selectedSub.cancelledAt) },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">{label}</p>
                  <div className="mt-1 text-sm font-medium text-gray-800">{value}</div>
                </div>
              ))}
            </div>
            {selectedSub.internalNote && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                <p className="text-xs font-medium text-amber-700 mb-1">{tx('Internal Note')}</p>
                <p className="text-sm text-amber-800">{selectedSub.internalNote}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{tx('Timeline')}</p>
              <Timeline
                items={[
                  { id: '1', title: tx('Subscription Created'), timestamp: selectedSub.createdAt, color: 'bg-indigo-100 text-indigo-600' },
                  ...(selectedSub.trialStartsAt ? [{ id: '2', title: tx('Trial Started'), timestamp: selectedSub.trialStartsAt, color: 'bg-blue-100 text-blue-600' }] : []),
                  ...(selectedSub.trialEndsAt ? [{ id: '3', title: tx('Trial Ends'), timestamp: selectedSub.trialEndsAt, color: 'bg-amber-100 text-amber-600' }] : []),
                  ...(selectedSub.cancelledAt ? [{ id: '4', title: tx('Cancelled'), timestamp: selectedSub.cancelledAt, color: 'bg-red-100 text-red-600' }] : []),
                ]}
              />
            </div>
            <div className="flex gap-2 pt-2 border-t border-gray-100">
              {can('subscriptions.change_plan') && (
              <button
                onClick={() => { setChangePlanModal(true); setNewPlanId(selectedSub.planId); setReason('') }}
                className="flex-1 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
              >
                {tx('Change Plan')}
              </button>
              )}
              {can('subscriptions.addons') && (
              <button
                onClick={() => { setAddonsModal(true); setAddonPick(selectedSub.addons ?? []); setAddonMonths(selectedSub.addonExpiresAt ? '1' : '0'); setReason('') }}
                className="flex-1 py-2 text-sm font-medium text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
              >
                {tx('AI Add-ons')}
              </button>
              )}
              {can('subscriptions.cancel') && selectedSub.status === 'trial' && (
              <button
                onClick={() => { setReason(''); setReactivateDialog(true) }}
                className="flex-1 py-2 text-sm font-medium text-green-600 border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
              >
                {tx('Activate')}
              </button>
              )}
              {can('subscriptions.extend') && (
              <button
                onClick={() => { setExtendModal(true); setExtendDays('30'); setReason('') }}
                className="flex-1 py-2 text-sm font-medium text-green-600 border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
              >
                {tx('Extend')}
              </button>
              )}
            </div>
          </div>
        )}
      </DetailDrawer>

      {/* AI Add-ons Modal */}
      {addonsModal && selectedSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setAddonsModal(false)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">{tx('AI Add-ons —')} {selectedSub.shopName}</h3>
            <p className="text-xs text-gray-500 mb-4">{tx('Grants the add-on directly (no payment record). Normal sales go through the shop paying in backoffice → Shop Payments approve.')}</p>
            <div className="space-y-2">
              {[
                { code: 'ai_growth', name: 'AI Growth', price: '990฿/เดือน', desc: tx('Daily brief, insights, staff coach, revenue growth') },
                { code: 'ai_crm', name: 'AI CRM', price: '1,490฿/เดือน', desc: tx('+ customer segments & win-back') },
                { code: 'ai_full', name: 'AI Full Suite', price: '2,490฿/เดือน', desc: tx('+ fraud / loss detection') },
              ].map((a) => (
                <label key={a.code} className={`flex items-start gap-3 border rounded-lg px-3 py-2 cursor-pointer ${addonPick.includes(a.code) ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={addonPick.includes(a.code)}
                    onChange={(e) => setAddonPick(e.target.checked ? [...addonPick, a.code] : addonPick.filter((x) => x !== a.code))}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between text-sm font-medium text-gray-900"><span>{a.name}</span><span className="text-gray-500">{a.price}</span></div>
                    <div className="text-xs text-gray-500">{a.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Duration')}</label>
              <select
                value={addonMonths}
                onChange={(e) => setAddonMonths(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="0">{tx('No expiry (until removed)')}</option>
                {[1, 2, 3, 6, 12].map((m) => <option key={m} value={String(m)}>{m} {tx('month(s) — then removed automatically')}</option>)}
              </select>
              {selectedSub.addonExpiresAt && <p className="text-xs text-gray-400 mt-1">{tx('Current expiry:')} {formatDate(selectedSub.addonExpiresAt)}</p>}
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Reason')} <span className="text-red-500">*</span></label>
              <textarea
                rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                placeholder={tx('e.g. free trial 1 month / paid by bank transfer')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setAddonsModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{tx('Cancel')}</button>
              <button
                onClick={() => addonsMutation.mutate()}
                disabled={!reason.trim() || addonsMutation.isPending}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {addonsMutation.isPending ? tx('Saving...') : tx('Save Add-ons')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {changePlanModal && selectedSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setChangePlanModal(false)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Change Plan —')} {selectedSub.shopName}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('New Plan')}</label>
                <select
                  value={newPlanId}
                  onChange={(e) => setNewPlanId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">{tx('Select plan...')}</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}{p.id === selectedSub.planId ? ' (current)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">{tx('Current plan:')} {selectedSub.planName}. Pick a different plan to change.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Reason')} <span className="text-red-500">*</span></label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  placeholder={tx('Reason for plan change...')}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setChangePlanModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{tx('Cancel')}</button>
              <button
                onClick={() => changePlanMutation.mutate()}
                disabled={!newPlanId || newPlanId === selectedSub.planId || !reason.trim() || changePlanMutation.isPending}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {changePlanMutation.isPending ? tx('Saving...') : tx('Change Plan')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Modal */}
      {extendModal && selectedSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setExtendModal(false)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Extend Subscription —')} {selectedSub.shopName}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{tx('Extend by')}</label>
                <div className="flex gap-2 flex-wrap">
                  {['30', '60', '90', 'custom'].map((d) => (
                    <button
                      key={d}
                      onClick={() => setExtendDays(d)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        extendDays === d
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {d === 'custom' ? 'Custom' : `${d} days`}
                    </button>
                  ))}
                </div>
                {extendDays === 'custom' && (
                  <input
                    type="number"
                    min="1"
                    placeholder={tx('Number of days')}
                    value={customDays}
                    onChange={(e) => setCustomDays(e.target.value)}
                    className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Reason')} <span className="text-red-500">*</span></label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  placeholder={tx('Reason for extension...')}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setExtendModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{tx('Cancel')}</button>
              <button
                onClick={() => extendMutation.mutate()}
                disabled={!reason.trim() || extendMutation.isPending}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {extendMutation.isPending ? 'Extending...' : tx('Extend')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={cancelDialog}
        onClose={() => setCancelDialog(false)}
        onConfirm={() => cancelMutation.mutate()}
        title={`Cancel subscription for "${selectedSub?.shopName}"`}
        description={tx('The shop will lose access at the end of their current billing period.')}
        confirmLabel={tx('Cancel Subscription')}
        variant="danger"
        requireReason
        reason={reason}
        onReasonChange={setReason}
        isLoading={cancelMutation.isPending}
      />

      <ConfirmDialog
        open={reactivateDialog}
        onClose={() => setReactivateDialog(false)}
        onConfirm={() => reactivateMutation.mutate()}
        title={selectedSub?.status === 'trial' ? `${tx('Activate (skip trial)')} — ${selectedSub?.shopName}` : `Reactivate subscription for "${selectedSub?.shopName}"`}
        description={selectedSub?.status === 'trial' ? tx('The shop becomes a paying (active) subscription on its current plan without a payment record. Use for test shops or manual sales.') : tx('This will restore the subscription and renew billing.')}
        confirmLabel={selectedSub?.status === 'trial' ? tx('Activate') : tx('Reactivate')}
        variant="default"
        requireReason
        reason={reason}
        onReasonChange={setReason}
        isLoading={reactivateMutation.isPending}
      />
    </div>
  )
}

export default SubscriptionsPage
